import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components/macro";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import getFirebase from "../../firebase";
import Print from "@material-ui/icons/Print";
import Apps from "@material-ui/icons/Apps";
import CloudUpload from "@material-ui/icons/CloudUpload";
import { sectionList } from "./printer";
import Switch from "@material-ui/core/Switch";
import { DEV_TOKEN_KEY, getDevToken, isDevMode } from "../utils/commHelper";
import { theme, ACTIVE_JOB_STATUSES } from "./theme";

const LeftPanel = ({
  printers,
  jobs,
  section,
  onSectionChange,
  isDev,
  canAccessDev,
  onToggleDev,
  userFullName,
}) => {
  const printerList = useMemo(() => Object.values(printers || {}), [printers]);
  const onlineCount = printerList.filter((p) => p.status === "OK").length;
  const totalCount = printerList.length;
  const fleetPercent = totalCount ? Math.round((onlineCount / totalCount) * 100) : 0;

  const activeJobsCount = (jobs || []).filter((j) =>
    ACTIVE_JOB_STATUSES.includes(j.status)
  ).length;

  // getAuth().currentUser is read synchronously here, but Firebase Auth hasn't
  // necessarily finished restoring its cached session by the time this first
  // renders — reading it once would silently stay null forever. Subscribe to
  // onAuthStateChanged so the sidebar updates the moment the real user resolves.
  const [currentUser, setCurrentUser] = useState(null);
  useEffect(() => {
    let unsubscribe = () => {};
    try {
      unsubscribe = onAuthStateChanged(getAuth(getFirebase()), setCurrentUser);
    } catch (e) {
      console.warn("[Printer] getAuth failed:", e?.message || e);
    }
    return () => unsubscribe();
  }, []);

  // userFullName comes from the app's own Users table (via /getUserPermissions) —
  // preferred over Firebase's displayName, which is usually unset for CS staff
  // logged in via email/custom-token rather than a social provider.
  const userName = userFullName || currentUser?.displayName || null;
  const userEmail = currentUser?.email || null;
  const userPhoto = currentUser?.photoURL || null;
  const userLabel = userName || userEmail || "Ops admin";
  const userInitials = userLabel
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "OA";

  // <Login> (App.jsx) gates on a Redux `user` object that's mirrored into
  // localStorage["user"] at sign-in (GoogleBtn.jsx) — Firebase signOut() alone
  // doesn't clear that, so the app would still think it's logged in. Clearing it
  // + reloading is the same effect a fresh unauthenticated load would have.
  const handleLogout = async () => {
    if (!window.confirm(`Log out of TULU Print${userLabel ? ` (${userLabel})` : ""}?`)) return;
    try {
      if (isDevMode() && getDevToken()) {
        localStorage.removeItem(DEV_TOKEN_KEY);
      } else {
        localStorage.removeItem("user");
        await getAuth(getFirebase()).signOut();
      }
    } catch (e) {
      console.warn("[Printer] logout failed:", e?.message || e);
    } finally {
      window.location.reload();
    }
  };

  return (
    <Wrapper>
      <Logo>
        <LogoDot />
        TULU Print<sup>&nbsp;beta</sup>
      </Logo>

      {/* Temporarily hidden: the Dev/Prod toggle re-sends the current (prod) Firebase
          token to api.dev.tulu.io, which doesn't resolve to a valid dev-env user — it
          needs its own re-auth flow (like login.jsx's DevTokenForm) before this is
          usable. Re-enable by restoring the condition below once that's wired up. */}
      {false && canAccessDev ? (
        <DevToggleRow>
          <span>Dev env</span>
          <Switch
            checked={!!isDev}
            onChange={(e) => onToggleDev?.(e.target.checked)}
            color="primary"
          />
        </DevToggleRow>
      ) : null}

      <NavList>
        <NavItem
          onClick={() => onSectionChange(sectionList.PRINT_JOBS)}
          isActive={section === sectionList.PRINT_JOBS}
        >
          <Print style={{ fontSize: 17 }} />
          <NavLabel>Print jobs</NavLabel>
          {activeJobsCount > 0 ? <NavBadge>{activeJobsCount}</NavBadge> : null}
        </NavItem>
        <NavItem
          onClick={() => onSectionChange(sectionList.PRINTERS)}
          isActive={section === sectionList.PRINTERS}
        >
          <Apps style={{ fontSize: 17 }} />
          <NavLabel>Printers</NavLabel>
        </NavItem>
        <NavItem
          onClick={() => onSectionChange(sectionList.PRINT_FILE)}
          isActive={section === sectionList.PRINT_FILE}
        >
          <CloudUpload style={{ fontSize: 17 }} />
          <NavLabel>Print file</NavLabel>
        </NavItem>
      </NavList>

      <FleetCard>
        <FleetTitle>Fleet</FleetTitle>
        <FleetCount>
          {onlineCount}<FleetCountTotal>/{totalCount} online</FleetCountTotal>
        </FleetCount>
        <FleetBarTrack>
          <FleetBarFill percent={fleetPercent} />
        </FleetBarTrack>
      </FleetCard>

      <Spacer />

      <UserRow onClick={handleLogout} title="Log out">
        {userPhoto ? (
          <UserAvatarImg src={userPhoto} alt={userLabel} />
        ) : (
          <UserAvatar>{userInitials}</UserAvatar>
        )}
        <UserInfo>
          <UserName>{userLabel}</UserName>
          {userName && userEmail ? <UserSub>{userEmail}</UserSub> : <UserSub>Log out</UserSub>}
        </UserInfo>
      </UserRow>
    </Wrapper>
  );
};

// position: fixed instead of sticky — sidesteps flexbox/sticky interaction quirks
// (stretch, containing-block edge cases) entirely. Taken out of normal flow, so
// printer.js compensates with matching left padding on its own Wrapper.
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 0;
  left: 0;
  width: 240px;
  height: 100vh;
  overflow-y: auto;
  background: ${theme.surface};
  color: ${theme.text};
  padding: 24px 16px;
  border-right: 1px solid ${theme.border};
  font-family: ${theme.fontFamily};
`;

const DevToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 4px;
  margin-bottom: 8px;
  font-size: 13px;
  color: ${theme.textMuted};
`;

const Logo = styled.p`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-left: 4px;
  color: ${theme.text};
  font-size: 19px;
  font-weight: 600;
  text-align: start;
  margin-bottom: 28px;

  sup {
    font-size: 11px;
    font-weight: 500;
    color: ${theme.textFaint};
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
`;

const LogoDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${theme.primary};
  flex-shrink: 0;
`;

const NavList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const NavItem = styled.button`
  display: flex;
  align-items: center;
  text-align: start;
  outline: none;
  border: none;
  cursor: pointer;
  font-family: ${theme.fontFamily};
  font-size: 14px;
  font-weight: ${({ isActive }) => (isActive ? 600 : 500)};
  color: ${({ isActive }) => (isActive ? theme.primaryHover : theme.textMuted)};
  background-color: ${({ isActive }) => (isActive ? theme.primaryLight : "transparent")};
  border-radius: ${theme.radiusSm};
  padding: 10px 14px;
  gap: 10px;
  transition: background-color 150ms ease, color 150ms ease;

  &:hover {
    background-color: ${theme.primaryLight};
    color: ${theme.primaryHover};
  }
`;

const NavLabel = styled.span`
  flex: 1;
`;

const NavBadge = styled.span`
  flex-shrink: 0;
  min-width: 18px;
  text-align: center;
  font-size: 11px;
  font-weight: 600;
  color: #ffffff;
  background: ${theme.primary};
  border-radius: ${theme.radiusPill};
  padding: 1px 6px;
`;

const FleetCard = styled.div`
  margin-top: 24px;
  padding: 14px;
  border-radius: ${theme.radiusSm};
  background: ${theme.background};
`;

const FleetTitle = styled.div`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${theme.textFaint};
  margin-bottom: 6px;
`;

const FleetCount = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: ${theme.text};
  margin-bottom: 8px;
`;

const FleetCountTotal = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${theme.textFaint};
  margin-left: 2px;
`;

const FleetBarTrack = styled.div`
  width: 100%;
  height: 6px;
  border-radius: ${theme.radiusPill};
  background: ${theme.borderStrong};
  overflow: hidden;
`;

const FleetBarFill = styled.div`
  width: ${({ percent }) => percent}%;
  height: 100%;
  border-radius: ${theme.radiusPill};
  background: ${theme.primary};
  transition: width 300ms ease;
`;

const Spacer = styled.div`
  flex: 1;
`;

const UserRow = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  gap: 10px;
  padding: 16px 4px 0;
  border: none;
  border-top: 1px solid ${theme.border};
  background: transparent;
  cursor: pointer;
  font-family: ${theme.fontFamily};
  text-align: start;
  border-radius: ${theme.radiusSm};
  transition: background-color 150ms ease;

  &:hover {
    background-color: ${theme.background};
  }
`;

const UserAvatar = styled.div`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  color: ${theme.primaryHover};
  background: ${theme.accentLight};
`;

const UserAvatarImg = styled.img`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 50%;
  object-fit: cover;
`;

const UserInfo = styled.div`
  min-width: 0;
`;

const UserName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${theme.text};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const UserSub = styled.div`
  font-size: 11px;
  color: ${theme.textFaint};
`;

export { LeftPanel };
